import React, {useState, useCallback, useEffect} from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useLazyQuery } from '@apollo/client';
import DataState from 'app/src/components/DataState';
import Input from 'app/src/elements/inputs';
import { Button, ButtonGroup } from 'app/src/elements/buttons';
import { View } from 'react-native';
import { SELLER, UPDATE_SELLER } from './queries';
import { ButtonContainer } from './elements';
import { useParams } from 'react-router-dom';
import { RELEASE_OPTIONS } from '.';

const EditSeller = ({setShowModal, refetch, withNav, sellerDetails}) => {
  const [submitting, setSubmitting] = useState();
  const [seller, setSeller] = useState(sellerDetails);
  const { id } = useParams();

  const [updateSeller] = useMutation(UPDATE_SELLER, {
    onCompleted: () => {
      refetch();
      setShowModal(false);
    }
  });

  const onSubmit = useCallback(input => {
    setSubmitting(true);
    updateSeller({ variables: input });
  });

  const [ loadSeller, { loading: loading, data: data } ] =
    useLazyQuery(SELLER, {onCompleted: refetch});

  if (seller === undefined && id && !loading) {
    loadSeller({ variables: { id } });
  }

  const { control, handleSubmit, watch, reset, register, formState: { errors } } = useForm({
    defaultValues: {
      id: id || seller?.id,
      name: seller?.name || '',
      companyName: seller?.companyName || '',
      street: seller?.street || '',
      city: seller?.city || '',
      state: seller?.state || '',
      postalCode: seller?.postalCode || '',
      phoneNumber: seller?.phoneNumber || '',
      primaryContactId: seller?.primaryContactId || '',
      feeType: seller?.feeType || 'Percent',
      feeAmount: seller?.feeAmount || '0',
      releaseOption: seller?.releaseOption || '',
      canAddAsset: seller?.canAddAsset || false,
      showDatePaid: seller?.showDatePaid || false,
      inspectionFeeAmount: seller?.inspectionFeeAmount || '0'
    }
  });

  useEffect(() => {
    if(data !== undefined && seller === undefined) {
      setSeller(data?.seller);
      reset(
        {
          id: id,
          name: data?.seller?.name,
          companyName: data?.seller?.companyName,
          street: data?.seller?.street,
          city: data?.seller?.city,
          state: data?.seller?.state,
          postalCode: data?.seller?.postalCode.formatted,
          phoneNumber: data?.seller?.phoneNumber.formatted,
          primaryContactId: data?.seller?.primaryContactId,
          feeType: data?.seller?.feeType,
          feeAmount: data?.seller?.feeAmount,
          releaseOption: data?.seller?.releaseOption,
          canAddAsset: data?.seller?.canAddAsset,
          showDatePaid: data?.seller?.showDatePaid,
          inspectionFeeAmount: data?.seller?.inspectionFeeAmount
        }
      )
    }
  }, [data])

  if(loading) {
    return <DataState.Loading />;
  }

  const watchFeeType = watch('feeType');
  const watchPaidDate = watch('showDatePaid');
  console.log("PAID DATE, YO?", watchPaidDate);

  return (
    <View>
      {submitting ?
        <DataState.Loading /> :
        <View>
          <Input.Text
            label="Name:"
            name="name"
            control={control}
            errors={errors}
          />
          <Input.Text
            label="Company Name:"
            hint="At least one of name or company name is required."
            name="companyName"
            control={control}
            errors={errors}
          />
          <Input.Text
            label="Street:"
            name="street"
            control={control}
            errors={errors}
          />
          <Input.Text
            label="City:"
            name="city"
            control={control}
            errors={errors}
          />
          <Input.StatePicker
            label="State:"
            name="state"
            control={control}
            errors={errors}
          />
          <Input.Text
            label="Zip:"
            name="postalCode"
            control={control}
            errors={errors}
          />
          <Input.Phone
            label="Phone Number:"
            name="phoneNumber"
            control={control}
            errors={errors}
          />
          <Input.Picker
            label="Seller Fee Type:"
            name="feeType"
            control={control}
            errors={errors}
            options={['Percent', 'Flat Fee'].map(option => {
              return {value: option, label: option}
            })}
          />
          <Input.Number
            label={
              `Seller Fee ${'Flat Fee' === watchFeeType ? 'Amount' : '%'}:`
            }
            name="feeAmount"
            control={control}
            errors={errors}
          />
          <Input.Number
            label={
              `Inspection Fee Amount:`
            }
            name="inspectionFeeAmount"
            control={control}
            errors={errors}
          />
          <Input.Picker
            label="Primary Contact"
            name="primaryContactId"
            control={control}
            errors={errors}
            options={seller?.contacts?.map(contact => {
              return {value: contact.id, label: `${contact.firstName} - ${contact.email}`}
            })}
          />
          <Input.Picker
            label="Release Option"
            name="releaseOption"
            control={control}
            errors={errors}
            options={
              RELEASE_OPTIONS.map(option => {
                return {value: option, label: option}
              })
            }
            {...register('releaseOption', { required: 'Required' })}
          />

          <Input.Checkbox
            label="Can This Seller Add Own Assets?"
            name="canAddAsset"
            control={control}
            errors={errors}
          />
          <Input.Checkbox
            label="Should this Seller see the Date Paid?"
            name="showDatePaid"
            control={control}
            errors={errors}
          />
          <ButtonContainer>
            <ButtonGroup>
              <Button label="Update Seller" onPress={handleSubmit(onSubmit)} />
            </ButtonGroup>
          </ButtonContainer>
        </View>
      }
    </View>
  )
}

export default EditSeller;
